<script>
    import { onMount } from "svelte";

    import { fly } from "svelte/transition";

    import {
        NotificationModal,
        PageLoading,
    } from "../../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../../Services/index.svelte";

    import { asaAllItemsList } from "../../../../stores";

    import CodeSelectorSearchAsa from "./asaComponents/CodeSelectorSearchASA.svelte";
    import CodeSelectorManualAsaContainer from "./asaComponents/CodeSelectorManualASAContainer.svelte";

    import ChangeItemTypeModal from "./common/ChangeItemTypeModal.svelte";

    import {
        getText,
        showError,
        showException,
    } from "../../../../Utils/utilFunctions.svelte";

    import { disableItemsMap } from "../../helpers/asaHelper.svelte";

    import { getPublicHolidays } from "../../../../Utils/DataLoaders.svelte";

    export let itemCodeObj = {},
        isSelectorOpen = false,
        procedureName = "",
        doctorId = 0,
        itemType = '',
        itemsData = [], // items already selected
        hasValidBillingData = false,
        saveBillingItem = () => {},
        hasActiveQuoteOrInvoice = false,
        clearItems = () => {},
        changeItemType = () => {},
        closeModal = () => {},
        isTypeA = false,
        isSavingItems = false,
        allowFreeTextCodes = false;

    $: if (isSelectorOpen) selectedItems = [...itemsData];
    else selectedItems = [];

    $: isAddBtnDisabled = _checkAddBtnDisabled(selectedItems, itemCodeObj);

    $: isLoadingData = isLoadingItems || isSyncingItems;

    const __DEFAULT_ITEM_TYPE__ = Number(1),
        __AUTOCLOSE_NOTIFICATION_TIMEOUT__ = 4000,
        __FLY_DURATION__ = 300;

    let isLoadingItems = true,
        isSyncingItems = true,
        selectedItems = [],
        holidaysInState = [],
        infoTypesMap = new Map();

    let timeObj = {};

    let isClearItemsConfirmationOpen = false,
        isClearingItems = false;

    let isNotificationOpen = false,
        autoCloseTimer,
        autoCloseCoolDown = __AUTOCLOSE_NOTIFICATION_TIMEOUT__,
        notificationText = "";

    let isSwitchItemTypeModalOpen = false,
        selectedItemType = __DEFAULT_ITEM_TYPE__;

    const modifierMap = new Map([]),
        noDisplayModifierItems = ["M1"];

    $: btnOptions = [
        {
            class: "btn !bg-white !shadow-none text-black",
            loading: isSavingItems || isClearingItems,
            text: "Clear all items",
            isHidden: hasActiveQuoteOrInvoice || selectedItems.length == 0,
            function: () => {
                isClearItemsConfirmationOpen = true;
            }
        },
        {
            class: "btn btn-secondary btn-error",
            loading: isSavingItems || isClearingItems,
            text: "Cancel",
            function: () => {
                closeModal();
            }
        },
        {
            class: "btn btn-secondary min-w-[100px]",
            loading: isAddBtnDisabled || isSavingItems,
            text: isSavingItems ? "Saving" : "Save & Refresh",
            function: () =>
                saveBillingItem(selectedItems, "add", itemCodeObj, timeObj),
        },
    ];

    $: headerOptions = [
        {
            optionText: "Change to use Free Text Items",
            iFont: "fa-solid fa-right-left",
            function: () => _openSwitchItemTypeModal(2),
            isDisabled: isLoadingData,
            isHidden: !allowFreeTextCodes,
            disabledTooltip: `You cannot change codes when ${itemType} is already created`
        },
        {
            optionText: "Change to MBS Codes",
            iFont: "fa-solid fa-right-left",
            function: () => _openSwitchItemTypeModal(0),
            isDisabled: isLoadingData || hasValidBillingData || isTypeA,
            disabledTooltip: `You cannot change codes when ${itemType} is already created`
        },
    ];

    onMount(async () => {
        infoTypesMap = new Map([
            ["Consults", []],
            ["Surgery", []],
            ["Modifiers", []],
            ["AdditionalProcedures", []],
            ["Time", []],
            ["Other", []], // infoType = ""
        ]);

        await loadData();

        if ($asaAllItemsList.length) _syncItemsByType();
    });

    const loadData = async () => {
        isLoadingItems = true;

        if (!$asaAllItemsList.length) await loadAsaItems();

        await loadHolidays();

        isLoadingItems = false;
    };

    const loadAsaItems = async () => {
        await apiMethods.getMethod
            .getASAItems(0, 999999)
            .then((res) => _handleLoadItems(res))
            .catch((err) => showException(err));
    };

    const loadHolidays = async () => {
        const holidayObj = {
            "filter[year]": itemCodeObj.scheduledDate
                ? new Date(itemCodeObj.scheduledDate).getFullYear()
                : new Date().getFullYear(),
        };

        if (itemCodeObj.patientState)
            holidayObj["filter[state]"] = itemCodeObj.patientState;

        await getPublicHolidays(0, 999999, holidayObj)
            .then((res) => _handleLoadHolidays(res))
            .catch((err) => showException(err));
    };

    const updateItems = (arr = []) => (selectedItems = [...arr]);

    const confirmSwitchItemType = (newItemType = Number(0)) => {
        changeItemType(newItemType);
        _closeSwitchItemTypeModal();
    };

    const openNotification = (itemCode = "", isKey = true) => {
        isNotificationOpen && closeNotification();

        if (!itemCode) notificationText = "Unable to add selected items";
        else {
            if (isKey) {
                const conflictItems = disableItemsMap.get(itemCode);
                notificationText = `Unable to select ${itemCode} when items ${conflictItems.join()} been selected`;
            } else {
                let itemKey = "";

                for (const [key, value] of disableItemsMap.entries()) {
                    if (value.includes(itemCode)) {
                        itemKey = key;
                        break;
                    }
                }

                notificationText = `Item ${itemCode} cannot be selected when ${itemKey} is selected`;
            }
        }

        setTimeout(() => {
            isNotificationOpen = true;

            _updateCountdown();
        }, __FLY_DURATION__);
    };

    const closeNotification = () => {
        isNotificationOpen = false;

        clearInterval(autoCloseTimer);

        autoCloseCoolDown = __AUTOCLOSE_NOTIFICATION_TIMEOUT__;
    };

    // HELPER FUNCTIONS
    const _handleLoadItems = (res) => {
        if (!res || res.error) {
            showError(
                "Failed to load items, please refresh the page and try again and contact support if the issue persists.",
            );
            closeModal();
        } else $asaAllItemsList = [...res.results];
    };

    const _handleLoadHolidays = (res) => {
        if (!res || res.error) {
            showError(
                "Failed to load holidays, please refresh the page and try again and contact support if the issue persists.",
            );
            closeModal();
        } else holidaysInState = [...res.results];
    };

    const _syncItemsByType = async () => {
        isSyncingItems = true;

        await $asaAllItemsList.map((item) => {
            const arr = item.infoType
                ? infoTypesMap.get(item.infoType)
                : infoTypesMap.get("Other");

            if (arr) {
                arr.push(item);

                if (item.infoType) infoTypesMap.set(item.infoType, arr);
                else infoTypesMap.set("Other", arr);
            }

            if (selectedItems.length) {
                const { itemCode, subItemCode, infoType } = item;

                selectedItems.map((item) => {
                    if (
                        item.itemCode == itemCode &&
                        item.subItemCode == subItemCode &&
                        item.infoType == infoType
                    ) {
                        const findItem = $asaAllItemsList.find(
                            (item) =>
                                item.itemCode == itemCode &&
                                item.subItemCode == subItemCode &&
                                item.infoType == infoType,
                        );
                        item.id = findItem.id;
                    }
                });
            }
        });

        if (infoTypesMap.has("Modifiers")) {
            const modifierItems = infoTypesMap.get("Modifiers");

            modifierItems.map((item) => {
                const { infoRegion } = item;

                if (!modifierMap.has(infoRegion))
                    modifierMap.set(infoRegion, [{ ...item }]);
                else {
                    const arr = modifierMap.get(infoRegion);
                    arr.push(item);
                    modifierMap.set(infoRegion, arr);
                }
            });
        }

        isSyncingItems = false;
    };

    const _checkAddBtnDisabled = (itemsArr, itemProps) => {
        const { billingType, startDate, endDate, neuraxialType } = itemProps;

        if (itemsArr.length) return false;

        if (billingType != 2) return true;

        if ((!startDate || !endDate) && neuraxialType) return false;

        return true;
    };

    const _openSwitchItemTypeModal = (
        itemType = Number(__DEFAULT_ITEM_TYPE__),
    ) => {
        selectedItemType = Number(itemType);
        isSwitchItemTypeModalOpen = true;
    };

    const _closeSwitchItemTypeModal = () => {
        isSwitchItemTypeModalOpen = false;
        selectedItemType = Number(__DEFAULT_ITEM_TYPE__);
    };

    const _updateCountdown = () => {
        clearInterval(autoCloseTimer);

        const countdown = () => {
            autoCloseCoolDown -= 1000;

            if (autoCloseCoolDown <= 0) closeNotification();
        };

        autoCloseTimer = setInterval(countdown, 1000);
    };
</script>

<NotificationModal
    isModalOpen={isSelectorOpen}
    title="Select ASA Codes"
    {closeModal}
    closeModalFunction={closeModal}
    modalMaxWidth="max-w-6xl"
    cancelText="Cancel"
    showCancelBtn={false}
    showConfirmBtn={false}
    {btnOptions}
    {headerOptions}
    isConfirming={isSavingItems}
    noContentPadding
>
    <div class="flex flex-col overflow-y-auto relative p-6">
        {#if isNotificationOpen}
            <div
                role="alert"
                class="alert shadow-lg absolute z-10"
                style="width: calc(100% - 1.5rem); left: 0.75rem; right: 0.75rem"
                transition:fly={{ duration: __FLY_DURATION__, y: -50 }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    class="stroke-info shrink-0 w-6 h-6"
                    ><path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path></svg
                >
                <p class="cursor-default">{notificationText}</p>
                <button
                    class="btn btn-secondary btn-sm"
                    on:click={closeNotification}
                >
                    Close (close in {Math.floor(autoCloseCoolDown / 1000)} s)
                </button>
            </div>
        {/if}

        <p class="px-4 mt-2 font-bold">
            Procedure Name: {getText(itemCodeObj.procedureName)}
        </p>

        <CodeSelectorSearchAsa
            bind:itemProps={itemCodeObj}
            {selectedItems}
            {holidaysInState}
            {infoTypesMap}
            {modifierMap}
            {noDisplayModifierItems}
            {updateItems}
            {openNotification}
        />

        <div class="divider px-4 my-0" />

        {#if isLoadingData}
            <PageLoading />
        {:else}
            <CodeSelectorManualAsaContainer
                bind:itemProps={itemCodeObj}
                bind:timeObj
                {doctorId}
                {procedureName}
                {selectedItems}
                {holidaysInState}
                {infoTypesMap}
                {modifierMap}
                {noDisplayModifierItems}
                {updateItems}
                {openNotification}
            />
        {/if}
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={isClearItemsConfirmationOpen}
    title="Confirmation"
    closeModal={() => isClearItemsConfirmationOpen = false }
    cancelText="Cancel"
    submitText="Confirm"
    confirmAction={() => {
        //clear items
        isClearingItems = true;
        var clearItemsSuccessful = clearItems();
        isClearItemsConfirmationOpen = false; //close confirmation
        isClearingItems = false;
        if(clearItemsSuccessful) {
            closeModal(); //close code selector
        }
    }}
    closeModalFunction={() => isClearItemsConfirmationOpen = null }
>
    <div class="">
        This action will clear all {itemType} items.
        <br/>This means that also billing rule will be cleared and the {itemType} totals will be reset.
        <br/><br/>Are you sure you want to proceed?
    </div>
</NotificationModal>

<ChangeItemTypeModal
    isModalOpen={isSwitchItemTypeModalOpen}
    {selectedItemType}
    confirmAction={_closeSwitchItemTypeModal}
    closeModal={() => confirmSwitchItemType(selectedItemType)}
    closeModalFunction={_closeSwitchItemTypeModal}
/>
